import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CaretDown,
  CaretRight,
  DotsSixVertical,
  Pencil,
  Trash,
} from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import { Modal } from 'components/Modals/Modal/Modal';
import Button from 'components/Button/Button';
import Icon from 'components/Icon/Icon';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import Tooltip from 'components/Tooltip/Tooltip';
import DndProvider from 'components/DragAndDrop/DndProvider/DndProvider';
import { DangerModal } from 'components/Modals/DangerModal/DangerModal';
import { Level, upLevel } from 'components/Modals/Modal/type';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { ICreateQuestionData } from 'types/ConfigurationQuestion.types';
import {
  ITestExtraPriceOption,
  ITestExtraPriceQuestion,
} from 'types/Configuration.types';
import Draggable from 'components/DragAndDrop/Draggable/Draggable';
import { moveItem } from 'pages/DealerPanel/DealerPanelModelsPage/DealerPanelCategory/ModelConfiguration/QuestionsManager/reorder';
import { QuestionFormModal } from 'pages/DealerPanel/DealerPanelModelsPage/DealerPanelCategory/ModelConfiguration/QuestionsManager/QuestionFormModal/QuestionFormModal';
import {
  Container,
  EmptyState,
  HelperText,
  InlineButtonRow,
  List,
  SectionLabel,
  Title,
} from 'pages/DealerPanel/DealerPanelModelsPage/DealerPanelCategory/ModelConfiguration/QuestionsManager/QuestionsManagerModal/QuestionsManager.styled';
import {
  DragHandle,
  IconButton,
  OptionsCount,
  OptionsPanel,
  Row,
  RowActions,
  RowLabel,
  RowMain,
  RowSubInfo,
  SlugBadge,
  ToggleCell,
  ToggleLabel,
} from 'pages/DealerPanel/DealerPanelModelsPage/DealerPanelCategory/ModelConfiguration/QuestionsManager/QuestionRow/QuestionRow.styled';
import { LocalOptionsManager } from './LocalOptionsManager';

interface ILocalExtraPriceManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  questions: ITestExtraPriceQuestion[];
  setQuestions: (questions: ITestExtraPriceQuestion[]) => void;
  modelName: string;
  currency: string;
  level: Level;
}

// In-memory questions/options editor for the test/preview flow. Mirrors the
// server QuestionsManager UI but never persists; the edited questions are sent
// inline with the test configuration so the supplier can preview pricing.
export const LocalExtraPriceManagerModal = ({
  isOpen,
  onClose,
  questions,
  setQuestions,
  modelName,
  currency,
  level,
}: ILocalExtraPriceManagerModalProps) => {
  const { t } = useTranslation();
  const { isPhone } = useBreakpointFlag();

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deletionIndex, setDeletionIndex] = useState<number | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingIndex(null);
  };

  const updateQuestionAt = (
    index: number,
    patch: Partial<ITestExtraPriceQuestion>
  ) =>
    setQuestions(
      questions.map((question, current) =>
        current === index ? { ...question, ...patch } : question
      )
    );

  const updateOptionsAt = (index: number, options: ITestExtraPriceOption[]) =>
    setQuestions(
      questions.map((question, current) =>
        current === index
          ? {
              ...question,
              options,
              // A question can't stay active without at least one option.
              is_active: options.length ? question.is_active : false,
            }
          : question
      )
    );

  const handleAdd = (data: ICreateQuestionData) => {
    setQuestions([
      ...questions,
      {
        label: data.label,
        slug: data.slug,
        is_required: false,
        is_active: false,
        options: [],
      },
    ]);
    closeForm();
  };

  const handleEditLabel = (label: string) => {
    if (editingIndex === null) return;
    updateQuestionAt(editingIndex, { label });
    closeForm();
  };

  const handleDragHover = (from: number, to: number) =>
    setQuestions(moveItem(questions, from, to));

  const editingQuestion =
    editingIndex !== null ? questions[editingIndex] : null;

  return (
    <Modal
      level={level}
      isOpen={isOpen}
      onClose={onClose}
      modalStyle={{
        margin: 'auto',
        width: isPhone ? '95%' : '720rem',
        minWidth: isPhone ? '95%' : 'auto',
        maxHeight: '85vh',
        overflowY: 'auto',
        position: 'fixed',
      }}
    >
      <Container>
        <Title>
          {t('Manage extra price questions for {{MODEL_NAME}}').replace(
            '{{MODEL_NAME}}',
            modelName
          )}
        </Title>

        <SectionLabel>{t('Questions')}</SectionLabel>
        <HelperText>
          {t(
            'These changes are only used to test this configuration and are not saved.'
          )}
        </HelperText>

        <DndProvider>
          {questions.length ? (
            <List>
              {questions.map((question, index) => {
                const optionsCount = question.options?.length ?? 0;
                const hasOptions = optionsCount > 0;
                return (
                  <Draggable
                    key={index}
                    type="local-question"
                    index={index}
                    id={index}
                    draggedId={null}
                    setDraggedId={() => undefined}
                    onDragHover={handleDragHover}
                    isDragDisabled={questions.length < 2}
                  >
                    <Row>
                      <DragHandle>
                        <Icon
                          svg={DotsSixVertical}
                          size={20}
                          color={COLORS.BORDER}
                          weight="bold"
                        />
                      </DragHandle>
                      <IconButton
                        onClick={() =>
                          setExpandedIndex((prev) =>
                            prev === index ? null : index
                          )
                        }
                      >
                        <Icon
                          svg={expandedIndex === index ? CaretDown : CaretRight}
                          size={18}
                          color={COLORS.BLACK}
                        />
                      </IconButton>
                      <RowMain>
                        <RowLabel>{question.label}</RowLabel>
                        <RowSubInfo>
                          <SlugBadge>{question.slug}</SlugBadge>
                          <OptionsCount>
                            {optionsCount === 1
                              ? t('1 option')
                              : t('{{COUNT}} options').replace(
                                  '{{COUNT}}',
                                  String(optionsCount)
                                )}
                          </OptionsCount>
                        </RowSubInfo>
                      </RowMain>
                      <RowActions>
                        <ToggleCell>
                          <ToggleLabel>{t('Required')}</ToggleLabel>
                          <ToggleSwitch
                            isChecked={question.is_required}
                            onClick={() =>
                              updateQuestionAt(index, {
                                is_required: !question.is_required,
                              })
                            }
                          />
                        </ToggleCell>
                        <ToggleCell>
                          <ToggleLabel>{t('Active')}</ToggleLabel>
                          <Tooltip
                            content={
                              !hasOptions
                                ? t(
                                    'Add at least one option before activating this question'
                                  )
                                : undefined
                            }
                          >
                            <ToggleSwitch
                              isChecked={question.is_active}
                              disabled={!hasOptions}
                              onClick={() =>
                                updateQuestionAt(index, {
                                  is_active: !question.is_active,
                                })
                              }
                            />
                          </Tooltip>
                        </ToggleCell>
                        <IconButton
                          onClick={() => {
                            setEditingIndex(index);
                            setIsFormOpen(true);
                          }}
                        >
                          <Icon svg={Pencil} size={20} color={COLORS.GREEN} />
                        </IconButton>
                        <IconButton onClick={() => setDeletionIndex(index)}>
                          <Icon svg={Trash} size={20} color={COLORS.RED} />
                        </IconButton>
                      </RowActions>
                    </Row>
                    {expandedIndex === index && (
                      <OptionsPanel>
                        <LocalOptionsManager
                          options={question.options}
                          setOptions={(options) =>
                            updateOptionsAt(index, options)
                          }
                          currency={currency}
                          level={level}
                        />
                      </OptionsPanel>
                    )}
                  </Draggable>
                );
              })}
            </List>
          ) : (
            <EmptyState>{t('No questions yet')}</EmptyState>
          )}
        </DndProvider>

        <InlineButtonRow>
          <Button
            primary
            label={t('Add question')}
            height="40rem"
            onClick={() => {
              setEditingIndex(null);
              setIsFormOpen(true);
            }}
          />
        </InlineButtonRow>
      </Container>

      <QuestionFormModal
        isOpen={isFormOpen}
        level={upLevel(level)}
        questionForEditing={editingQuestion}
        onClose={closeForm}
        onAdd={handleAdd}
        onEditLabel={handleEditLabel}
        isLoading={false}
      />

      <DangerModal
        level={upLevel(level)}
        isOpen={deletionIndex !== null}
        setIsOpen={(open) => !open && setDeletionIndex(null)}
        title={t('Delete question')}
        description={t(
          'Are you sure you want to delete this question and all of its options?'
        )}
        submit={{
          onClick: () => {
            if (deletionIndex !== null) {
              setQuestions(
                questions.filter((_, index) => index !== deletionIndex)
              );
            }
            setDeletionIndex(null);
          },
          text: t('Confirm'),
          disabled: false,
        }}
        cancel={{ onClick: () => setDeletionIndex(null) }}
      />
    </Modal>
  );
};
